import { Button } from '@mui/material';
import type { SxProps } from '@mui/system';
import { GridCsvExportMenuItem, GridToolbarExportContainer } from '@mui/x-data-grid';
import { keys } from 'lodash';
import React, { Fragment, type ReactElement } from 'react';
import { useShallowMergeSxProps } from '../hooks/useShallowMergeSxProps';
import I18n from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { H } from '../Layout';
import type { Toolbar, ToolbarOptions } from './DataGrid/types';

const TOOLBAR_SX = {
    boxSizing: 'border-box',
    py: 1,
    pl: 1,
    pr: 2
} as const;

type CustomToolbarProps = {
    toolbar: Toolbar;
    toolbarOptions: ToolbarOptions;
    toolbarSx?: SxProps;
    createNewRow: () => void;
    isAddDisabled: boolean;
    addTitle?: string;
    ColumnVisibilitySelector: ReactElement;
    /** Optional sizing settings gear (`preserveConfiguration`). Rendered on the right. */
    columnSizingSettings?: ReactElement;
    /** Grid settings gear. Rendered on the right. */
    gridSettings?: ReactElement;
};

export const CustomToolbar = ({
    toolbar,
    toolbarOptions,
    toolbarSx,
    createNewRow,
    isAddDisabled,
    addTitle,
    ColumnVisibilitySelector,
    columnSizingSettings,
    gridSettings
}: CustomToolbarProps) => {
    const toolbarContainerSx = useShallowMergeSxProps(TOOLBAR_SX, toolbarSx);

    const toolBarComponents = {
        add: (
            <Button
                onClick={createNewRow}
                disabled={isAddDisabled}
                key={'add'}
                size={'small'}
                startIcon={<Icons.AddOutlined />}
            >
                {addTitle || <I18n token={'grid.button.add'} />}
            </Button>
        ),
        export: (
            <GridToolbarExportContainer key={'export'}>
                <GridCsvExportMenuItem options={toolbarOptions.csvOptions} />
            </GridToolbarExportContainer>
        ),
        custom: (
            <>
                {toolbar.custom?.map((tb, index) => (
                    <Fragment key={`custom-${index}`}>{tb}</Fragment>
                ))}
            </>
        ),
        columns: (
            <Button key={'columns'} size={'small'} startIcon={<Icons.Columns />}>
                <I18n token="common.columns" />
            </Button>
        ),
        presets: <>{toolbar.presets?.visible && ColumnVisibilitySelector}</>
    };

    const hasToolbarItems =
        toolbar.columns || toolbar.export || toolbar.add || toolbar.custom || toolbar.presets?.visible;

    if (!hasToolbarItems && !columnSizingSettings && !gridSettings) {
        return <Fragment />;
    }

    return (
        <H gap={1} sx={toolbarContainerSx}>
            <H gap={1} grow sx={{ minWidth: 0 }}>
                {keys(toolbar)?.map((toolbarOption, index) => (
                    <Fragment key={index}>{toolbar[toolbarOption] && toolBarComponents[toolbarOption]}</Fragment>
                ))}
            </H>
            {columnSizingSettings || gridSettings ? (
                <H gap={1} sx={{ width: 'auto', flexShrink: 0 }}>
                    {gridSettings}
                    {columnSizingSettings}
                </H>
            ) : null}
        </H>
    );
};
